import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { 
    Activity, Hand, Footprints, CircleDot, Bone,
    ChevronRight, Armchair, Zap, Circle, Spline
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom SVG icons for better distinction
const KneeIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4" />
        <path d="M12 18v4" />
        <circle cx="12" cy="12" r="4" />
        <path d="M8 12H4" />
        <path d="M20 12h-4" />
        <path d="M9 9L6 6" />
        <path d="M18 18l-3-3" />
        <path d="M15 9l3-3" />
        <path d="M6 18l3-3" />
    </svg>
);

const SpineIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="2" width="6" height="3" rx="1" />
        <rect x="8" y="6" width="8" height="3" rx="1" />
        <rect x="7" y="10" width="10" height="3" rx="1" />
        <rect x="8" y="14" width="8" height="3" rx="1" />
        <rect x="9" y="18" width="6" height="4" rx="1" />
    </svg>
);

const ElbowIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4l6 6" />
        <circle cx="12" cy="12" r="3" />
        <path d="M14 14l6 6" />
        <path d="M9 15c-1 2-2 4-5 5" />
        <path d="M15 9c2-1 4-2 5-5" />
    </svg>
);

const bodyPartIcons = {
    hip: Bone,
    knee: KneeIcon,
    shoulder: CircleDot,
    ankle: Footprints,
    foot: Footprints,
    spine: SpineIcon,
    hand: Hand,
    elbow: ElbowIcon,
    wrist: Hand
};

const bodyPartLabels = {
    hip: 'Hip',
    knee: 'Knee',
    shoulder: 'Shoulder',
    elbow: 'Elbow',
    wrist: 'Wrist',
    hand: 'Hand',
    ankle: 'Ankle',
    foot: 'Foot',
    spine: 'Spine'
};

export default function BodyPartStep() {
    const { body_part, condition, setBodyPart, setCondition } = useReferral();
    const [conditions, setConditions] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchConditions();
    }, []);

    const fetchConditions = async () => {
        try {
            const response = await axios.get(`${API}/conditions`);
            setConditions(response.data);
        } catch (error) {
            console.error('Failed to fetch conditions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBodyPartSelect = (part) => {
        setBodyPart(part);
    };

    const handleConditionSelect = (conditionId) => {
        setCondition(conditionId);
    };

    const availableConditions = body_part ? conditions[body_part] || [] : [];

    return (
        <div className="space-y-6">
            {/* Body Part Selection */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Activity className="w-5 h-5 text-teal-600" />
                        Select Body Part
                    </CardTitle>
                    <CardDescription>
                        Choose the anatomical region for this referral
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.keys(bodyPartLabels).map((part) => {
                            const Icon = bodyPartIcons[part];
                            const isSelected = body_part === part;
                            return (
                                <button
                                    key={part}
                                    onClick={() => handleBodyPartSelect(part)}
                                    className={`body-part-btn flex flex-col items-center justify-center p-6 ${
                                        isSelected ? 'selected' : ''
                                    }`}
                                    data-testid={`body-part-${part}`}
                                >
                                    <Icon className={`w-8 h-8 mb-3 transition-colors ${
                                        isSelected ? 'text-teal-600' : 'text-slate-400'
                                    }`} />
                                    <span className={`text-sm font-medium ${
                                        isSelected ? 'text-teal-700' : 'text-slate-700'
                                    }`}>
                                        {bodyPartLabels[part]}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Condition Selection */}
            {body_part && (
                <Card className="card-clinical animate-fadeIn">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ChevronRight className="w-5 h-5 text-teal-600" />
                            Select Condition
                        </CardTitle>
                        <CardDescription>
                            Choose the specific diagnosis for the {bodyPartLabels[body_part].toLowerCase()}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="py-8 text-center text-slate-500">
                                Loading conditions...
                            </div>
                        ) : availableConditions.length === 0 ? (
                            <div className="py-8 text-center text-slate-500">
                                No conditions available for this body part
                            </div>
                        ) : (
                            <RadioGroup 
                                value={condition} 
                                onValueChange={handleConditionSelect}
                                className="space-y-3"
                            >
                                {availableConditions.map((cond) => (
                                    <div
                                        key={cond.id}
                                        className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                                            condition === cond.id 
                                                ? 'border-teal-500 bg-teal-50' 
                                                : 'border-slate-200 hover:border-slate-300 bg-white'
                                        }`}
                                        onClick={() => handleConditionSelect(cond.id)}
                                        data-testid={`condition-${cond.id}`}
                                    >
                                        <RadioGroupItem value={cond.id} id={cond.id} />
                                        <Label 
                                            htmlFor={cond.id} 
                                            className="flex-1 cursor-pointer font-medium text-slate-700"
                                        >
                                            {cond.name}
                                        </Label>
                                        {cond.questionnaire && (
                                            <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                                                {cond.questionnaire.toUpperCase().replace('_', ' ')}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </RadioGroup>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Guidelines Info */}
            {condition && (
                <Card className="card-clinical bg-teal-50/50 border-teal-200 animate-fadeIn">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-teal-100 rounded-lg">
                                <Activity className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-teal-900 mb-1">
                                    Guideline-Based Assessment
                                </h4>
                                <p className="text-sm text-teal-700">
                                    This referral will be screened against BOA, NICE, and NHS guidelines, 
                                    as well as specialist society recommendations 
                                    (BOFAS, BESS, BSSH, RCSEng as applicable).
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
